import { CallRecordingSetting } from '@weave/schema-gen-ts/dist/schemas/phone/call-config/v2/call_config.pb';
import { CallRecordingApi, CallRecordingQueryKeys } from '@frontend/api-call-recording';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { useBackfillShallowStore } from './use-backfill-store';
import { useCallIntelTrial } from './use-call-intel-trial';

/**
 * Determines whether the recordings-disabled global banner should be visible.
 *
 * Conditions (all must be true):
 * - User is NOT on the Call Intelligence route (page-level error handles it there)
 * - At least one location has an active CI trial
 * - User has CALL_INTELLIGENCE_READ permission on trial locations
 * - Call recordings are disabled (setting === NONE)
 * - Backfill is still before first batch completion
 * - Banner has not been dismissed this session
 */
export const useBackfillRecordingsBannerVisibility = () => {
  const { hasTrialAccess, isOnCallIntelligenceRoute, hasTrialLocations, isWaitingForLiveCalls, showLoadingState } =
    useCallIntelTrial();
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const { dismissRecordingsBanner, checkRecordingsBannerDismiss } = useBackfillShallowStore(
    'dismissRecordingsBanner',
    'checkRecordingsBannerDismiss'
  );

  const isBeforeFirstBatchComplete = showLoadingState || isWaitingForLiveCalls;

  const primaryLocationId = selectedLocationIds[0];
  const tenantId = accessibleLocationData[primaryLocationId]?.phoneTenantId || '';

  const { data: callRecordingSettings } = useScopedQuery({
    queryKey: CallRecordingQueryKeys.listCallRecording(tenantId),
    queryFn: () => CallRecordingApi.GetCallRecording({ tenantId }),
    enabled: !!tenantId && hasTrialLocations,
  });

  const isCallRecordingDisabled = callRecordingSettings?.setting === CallRecordingSetting.NONE;

  const isVisible =
    !isOnCallIntelligenceRoute &&
    hasTrialLocations &&
    hasTrialAccess &&
    isBeforeFirstBatchComplete &&
    isCallRecordingDisabled &&
    !checkRecordingsBannerDismiss();

  return {
    dismiss: dismissRecordingsBanner,
    isVisible,
  };
};
